// Stimulus controller registrations
// Large, page-specific controllers are lazy-loaded via dynamic import()
// to reduce the initial JS bundle size on pages that don't use them.

import { application } from "./application"
import { Controller } from "@hotwired/stimulus"

// --- Lazy loading helper ---
// Registers a lightweight stub that dynamically imports the real controller
// on first connect, then replaces itself so subsequent connections use the
// real controller directly.
function lazyLoad(name, importFn) {
  application.register(name, class extends Controller {
    async connect() {
      const module = await importFn()
      application.unload(name)
      application.register(name, module.default)
    }
  })
}

// --- Eagerly loaded controllers (used on most pages) ---

import AddressAutocompleteController from "./address_autocomplete_controller"
application.register("address-autocomplete", AddressAutocompleteController)

import AuthSwitcherController from "./auth_switcher_controller"
application.register("auth-switcher", AuthSwitcherController)


import AutoSubmitController from "./auto_submit_controller"
application.register("auto-submit", AutoSubmitController)

import BookingDatesController from "./booking_dates_controller"
application.register("booking-dates", BookingDatesController)

import ScrollMemoryController from "./scroll_memory_controller"
application.register("scroll-memory", ScrollMemoryController)

import CarouselController from "./carousel_controller"
application.register("carousel", CarouselController)

import ComparisonTableController from "./comparison_table_controller"
application.register("comparison-table", ComparisonTableController)

import CounterController from "./counter_controller"
application.register("counter", CounterController)

import DateCarouselController from "./date_carousel_controller"
application.register("date-carousel", DateCarouselController)

import DatepickerController from "./datepicker_controller"
application.register("datepicker", DatepickerController)

import DocumentUploadController from "./document_upload_controller"
application.register("document-upload", DocumentUploadController)

import DropdownController from "./dropdown_controller"
application.register("dropdown", DropdownController)

import ExpandableTextController from "./expandable_text_controller"
application.register("expandable-text", ExpandableTextController)

import FileUploadController from "./file_upload_controller"
application.register("file-upload", FileUploadController)

import FlashController from "./flash_controller"
application.register("flash", FlashController)

import FooterAccordionController from "./footer_accordion_controller"
application.register("footer-accordion", FooterAccordionController)

import ImagePreviewController from "./image_preview_controller"
application.register("image-preview", ImagePreviewController)

import LazySectionController from "./lazy_section_controller"
application.register("lazy-section", LazySectionController)

import LocationFilterController from "./location_filter_controller"
application.register("location-filter", LocationFilterController)

import MapController from "./map_controller"
application.register("map", MapController)

import MarqueeController from "./marquee_controller"
application.register("marquee", MarqueeController)

import ModalController from "./modal_controller"
application.register("modal", ModalController)

import MultiplyController from "./multiply_controller"
application.register("multiply", MultiplyController)

import OrderNumberToggleController from "./order_number_toggle_controller"
application.register("order-number-toggle", OrderNumberToggleController)

import OrderFooterController from "./order_footer_controller"
application.register("order-footer", OrderFooterController)

import ParallaxController from "./parallax_controller"
application.register("parallax", ParallaxController)

import PollRefreshController from "./poll_refresh_controller"
application.register("poll-refresh", PollRefreshController)

import PetsDisplayController from "./pets_display_controller"
application.register("pets-display", PetsDisplayController)

import RecaptchaController from "./recaptcha_controller"
application.register("recaptcha", RecaptchaController)

import ReloadController from "./reload_controller"
application.register("reload", ReloadController)

import RequestController from "./request_controller"
application.register("request", RequestController)

import ScrollRevealController from "./scroll_reveal_controller"
application.register("scroll-reveal", ScrollRevealController)

import ScrollToActiveController from "./scroll_to_active_controller"
application.register("scroll-to-active", ScrollToActiveController)

import SearchFormController from "./search_form_controller"
application.register("search-form", SearchFormController)

import SegmentedInputController from "./segmented_input_controller"
application.register("segmented-input", SegmentedInputController)

import SlideOverController from "./slide_over_controller"
application.register("slide-over", SlideOverController)

import SortableColumnController from "./sortable_column_controller"
application.register("sortable-column", SortableColumnController)

import ToggleController from "./toggle_controller"
application.register("toggle", ToggleController)

import VideoModalController from "./video_modal_controller"
application.register("video-modal", VideoModalController)

// --- Lazy-loaded controllers (page-specific, large bundles) ---
// These are only downloaded when a matching data-controller element connects.

lazyLoad("activity-modal", () => import("./activity_modal_controller"))
lazyLoad("hero-video", () => import("./hero_video_controller"))
lazyLoad("homepage-search", () => import("./homepage_search_controller"))
lazyLoad("contract-form", () => import("./contract_form_controller"))
lazyLoad("daycare-calendar", () => import("./daycare_calendar_controller"))
lazyLoad("enhanced-conversion", () => import("./enhanced_conversion_controller"))
lazyLoad("conversion-event", () => import("./conversion_event_controller"))
lazyLoad("signature-pad", () => import("./signature_pad_controller"))
lazyLoad("daycare-date-select", () => import("./daycare_date_select_controller"))
lazyLoad("daycare-transport", () => import("./daycare_transport_controller"))
lazyLoad("guest-services", () => import("./guest_services_controller"))
lazyLoad("guest-services-search", () => import("./guest_services_search_controller"))
lazyLoad("recurring-schedule", () => import("./recurring_schedule_controller"))
lazyLoad("mailchimp-export", () => import("./mailchimp_export_controller"))
lazyLoad("resort-map", () => import("./resort_map_controller"))
lazyLoad("template-editor", () => import("./template_editor_controller"))
